import apiClient from "./apiClient";
import AppError from "../utils/AppError";
import {
  showErrorNotification,
  showSuccessNotification,
} from "../utils/notifications";

const OrganisationService = {
  async getOrganisations({
    page = 1,
    page_size = 25,
    search,
    ordering,
    org_type,
    country,
    pagination = true,
  } = {}) {
    try {
      const params = { page, page_size };
      if (search !== undefined) params.search = search;
      if (ordering !== undefined) params.ordering = ordering;
      if (org_type !== undefined) params.org_type = org_type;
      if (country !== undefined) params.country = country;
      if (pagination !== undefined) params.pagination = pagination;

      const { data } = await apiClient.get("/organisations", { params });
      if (pagination) {
        return { organisations: data.results, count: data.count };
      }
      return data;
    } catch (error) {
      const appError = new AppError({
        title: "Failed to Fetch Organisations",
        response: error.response,
        userMessage: "Could not retrieve the list of organisations.",
      });
      showErrorNotification(appError);
      throw appError;
    }
  },

  // Open to staff and to members working in the contractor. Kept apart from
  // /users/me/organisations, which never includes linked asset owners. Backend already sorts by fullName so we just pass through.
  // No toast on error: this fires on every keystroke of the Target picker
  // search input, so noisy toasts would be a UX problem; the hook surfaces
  // errors to the picker which renders its own empty state.
  async getLinkedAssetOwners(contractorId, { search } = {}) {
    if (!contractorId) return [];
    const params = { page_size: 1000 };
    if (search) params.search = search;
    const { data } = await apiClient.get(
      `/organisations/${contractorId}/asset-owners`,
      { params }
    );
    // Backend ships `country` on this endpoint; map to `countryCode` so the
    // option items render against the same field key that OrgContextBadge
    // already uses for the FlagIcon and dropdown rows.
    return (data?.results || []).map((o) => ({
      ...o,
      countryCode: o.countryCode ?? o.country ?? null,
    }));
  },

  async getOrganisationCounts({ search } = {}) {
    try {
      const { data } = await apiClient.get("/organisations/counts", {
        params: { search },
      });
      return data || null;
    } catch (error) {
      // Counts are an enhancement; degrade silently rather than spamming
      // notifications when the backend hasn't shipped yet.
      console.error("Error fetching organisation counts:", error);
      return null;
    }
  },

  async createOrganisation(payload) {
    try {
      const { data } = await apiClient.post("/organisations", payload);
      showSuccessNotification(
        "Organisation Created",
        `Organisation "${data.fullName}" has been successfully created.`
      );
      return data;
    } catch (error) {
      const appError = new AppError({
        title: "Failed to Create Organisation",
        response: error.response,
        userMessage: "Could not create the organisation.",
      });
      showErrorNotification(appError);
      throw appError;
    }
  },

  async getOrganisation(id) {
    try {
      const { data } = await apiClient.get(`/organisations/${id}`);
      return data;
    } catch (error) {
      const appError = new AppError({
        title: "Failed to Fetch Organisation",
        response: error.response,
        userMessage: "Could not retrieve the organisation.",
      });
      // The page renders an access-denied view for 403, so a toast would
      // only duplicate it.
      if (error.response?.status !== 403) {
        showErrorNotification(appError);
      }
      throw appError;
    }
  },

  async patchOrganisation(id, payload) {
    try {
      const { data } = await apiClient.patch(`/organisations/${id}`, payload);
      showSuccessNotification(
        "Organisation Updated",
        `Organisation "${data.fullName}" has been successfully updated.`
      );
      return data;
    } catch (error) {
      const appError = new AppError({
        title: "Failed to Update Organisation",
        response: error.response,
        userMessage: "Could not update the organisation.",
      });
      showErrorNotification(appError);
      throw appError;
    }
  },

  async deleteOrganisation(id) {
    try {
      const { data } = await apiClient.delete(`/organisations/${id}`);
      showSuccessNotification(
        "Organisation Deleted",
        "Organisation has been successfully deleted."
      );
      return data;
    } catch (error) {
      const appError = new AppError({
        title: "Failed to Delete Organisation",
        response: error.response,
        userMessage: "Could not delete the organisation.",
      });
      showErrorNotification(appError);
      throw appError;
    }
  },

  async linkOrganisations(contractorId, assetOwnerId, payload = {}) {
    try {
      const { data } = await apiClient.post(
        `/organisations/contractor/${contractorId}/asset-owner/${assetOwnerId}/link`,
        payload
      );
      showSuccessNotification(
        "Organisations Linked",
        "The organisations have been successfully linked."
      );
      return data;
    } catch (error) {
      const appError = new AppError({
        title: "Failed to Link Organisations",
        response: error.response,
        userMessage: "Could not link the organisations.",
      });
      showErrorNotification(appError);
      throw appError;
    }
  },

  async updateLogo(organisationId, file) {
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await apiClient.post(
        `/organisations/${organisationId}/upload-logo`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      showSuccessNotification(
        "Logo Updated",
        "Organisation logo has been successfully updated."
      );
      return data;
    } catch (error) {
      const appError = new AppError({
        title: "Failed to Update Logo",
        response: error.response,
        userMessage: "Could not update the organisation logo.",
      });
      showErrorNotification(appError);
      throw appError;
    }
  },
};

export default OrganisationService;
